// Papéis de acesso. Acesso total: admin, diretor, pricing, gerente_nacional.
// Escopo por hierarquia (só os vendedores deles): gerente, coordenador, supervisor.
export type Role = 'admin' | 'diretor' | 'pricing' | 'gerente_nacional' | 'gerente' | 'coordenador' | 'supervisor' | 'seller';

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  diretor: 'Diretor',
  pricing: 'Pricing',
  gerente_nacional: 'Gerente Nacional',
  gerente: 'Gerente',
  coordenador: 'Coordenador',
  supervisor: 'Supervisor',
  seller: 'Vendedor',
};
export const roleLabel = (r?: string): string => (ROLE_LABELS as Record<string, string>)[r || ''] || r || '—';

// Hierarquia do vendedor (TGFVEN): cada valor é o CODVEND do gestor correspondente.
export interface SellerHierarquia {
  pricing?: number | null;     // AD_CODUSUPRI
  gernac?: number | null;      // AD_CODGERNAC
  gerente?: number | null;     // CODGER
  coordenador?: number | null; // AD_CODCOR
  supervisor?: number | null;  // AD_CODSUP
  hea?: number | null;         // AD_CODHEA (nível intermediário)
  nivel?: number | null;       // AD_HIERARQUIA do próprio (1=vendedor,3=pricing,4=supervisor,5=coordenador,6=gerente_nacional,10=gerente,15=diretor)
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  phone?: string;
  codVendSankhya?: number; // Código no Sankhya (CODVEND) — vendedor OU código do gestor
  hierarquia?: SellerHierarquia; // preenchido pelo sincronizarHierarquia (TGFVEN)
  equipeCodvends?: number[]; // CODVENDs da equipe do gestor, vindos do Sankhya (sincronizarEquipes) — inclui vendedores que não são usuários do app
  equipeCodvendsStr?: string[]; // os MESMOS codigos em string (sankhya.codvend existe nos dois tipos)
  equipeSellerIds?: string[];   // uids dos vendedores da equipe + o proprio gestor; e o que as firestore.rules validam
  pushToken?: string;      // Expo push token (notificações)
  appVersion?: string;     // versão do app que o usuário está rodando (gravada no login)
  // Placa do veículo, SEM separador e em maiúsculas ('ABC1D23'). Preenchida pelo PRÓPRIO vendedor no
  // Meu Perfil do app — é por pessoa porque o rodízio de SP depende do carro de cada um. Só o último
  // dígito importa (ver `utils/rodizioSp`). A firestore.rules libera este campo no self-update.
  placaVeiculo?: string;
  ultimoAcesso?: Date;     // último acesso ao app (serverTimestamp, atualizado no login)
  createdAt: Date;
  active: boolean;
}

// Perfis que aparecem no FILTRO por vendedor (Dashboard/Histórico): todo mundo que trabalha em campo —
// não só 'seller'; supervisor/coordenador/gerente/GN/pricing/diretor também fazem check-in.
// (Espelha mobile/src/types/index.ts. O DashboardPage re-exporta daqui, p/ ter uma fonte só.)
export const PAPEIS_FILTRAVEIS = new Set<string>(['seller', 'supervisor', 'coordenador', 'gerente', 'gerente_nacional', 'pricing', 'diretor']);

// Acesso total a todos os dados (sem filtro por hierarquia).
export function isFullAccess(role?: string): boolean {
  return role === 'admin' || role === 'diretor' || role === 'pricing' || role === 'gerente_nacional';
}
// Gestor com escopo restrito aos vendedores da hierarquia dele.
export function isScopedManager(role?: string): boolean {
  return role === 'gerente' || role === 'coordenador' || role === 'supervisor';
}
export interface AccessScope {
  full: boolean;              // true = vê tudo (sem filtro)
  sellerIds: string[] | null; // uids permitidos (null = todos)
  codvends: string[] | null;  // CODVENDs permitidos (null = todos)
}

// Vendedores (users role='seller') que o gestor pode ver/gerenciar. Espelha o filtro do Sankhya:
// o CÓDIGO do gestor (codVendSankhya) aparece em QUALQUER campo de gestor da hierarquia do
// vendedor — CODGER (gerente) OU AD_CODCOR (coordenador) OU AD_CODSUP (supervisor) OU AD_CODHEA.
// Independe do nível/role exato do gestor (antes casava só o campo do papel dele, o que falhava
// quando o código estava em outro campo).
export function managedSellers(user: Pick<User, 'role' | 'id' | 'codVendSankhya'> | null, allSellers: User[]): User[] {
  if (!user) return [];
  // GESTOR QUE TAMBÉM RODA CARTEIRA PRÓPRIA: nesta operação supervisor/gerente/GN têm CODVEND e
  // clientes seus (o supervisor 915 tem 8). Filtrar por `role === 'seller'` os excluía tanto da
  // PRÓPRIA lista quanto da lista de quem está ACIMA deles — ninguém conseguia gerar rota para eles.
  // PAPEIS_FILTRAVEIS é a mesma peneira que o Dashboard/Histórico já usam ("quem trabalha em campo").
  const sellers = allSellers.filter((s) => PAPEIS_FILTRAVEIS.has(s.role));
  // O próprio gestor não casa na peneira de hierarquia abaixo (a hierarquia dele não aponta para ele
  // mesmo), então ele entra à parte — em 1º e sem duplicar.
  const eu = user.codVendSankhya != null ? allSellers.find((s) => s.id === user.id) : undefined;
  const comEu = (l: User[]) => (eu ? [eu, ...l.filter((s) => s.id !== eu.id)] : l);
  if (isFullAccess(user.role)) return comEu(sellers);
  if (user.role === 'seller') return sellers.filter((s) => s.id === user.id);
  const code = user.codVendSankhya;
  if (code == null) return [];
  const cs = String(code);
  const bate = (v?: number | null) => v != null && String(v) === cs;
  // Os SEIS campos de chefia, iguais aos que o servidor usa para montar `equipeSellerIds`
  // (functions/index.js `_computarEquipes`). Com menos campos aqui a lista ficava mais estreita que
  // o escopo que as regras já provam — vendedor no escopo, mas invisível no seletor.
  return comEu(sellers.filter((s) => {
    const h = s.hierarquia;
    return !!h && (bate(h.gerente) || bate(h.coordenador) || bate(h.supervisor) || bate(h.hea) || bate(h.gernac) || bate(h.pricing));
  }));
}

// Escopo de acesso do usuário logado (para filtrar clientes/check-ins/etc.).
// `allSellers` NÃO é mais lido: o escopo vem inteiro do que o servidor gravou no doc do usuário
// (`equipeCodvends`/`equipeSellerIds`). O parâmetro segue na assinatura só para não mexer nos
// chamadores; NÃO volte a calcular escopo a partir dele (veja o porquê no comentário do fallback).
export function getAccessScope(user: User | null, allSellers?: User[]): AccessScope {
  if (!user || isFullAccess(user.role)) return { full: true, sellerIds: null, codvends: null };
  // Vendedor: o escopo é ELE MESMO — não depende de ler a coleção `users` (que ele nem pode listar).
  // Sem isto, o filtro de Histórico (checkins.filter por scope.sellerIds) zerava as próprias visitas dele.
  if (user.role === 'seller') {
    return { full: false, sellerIds: [user.id], codvends: [String(user.codVendSankhya ?? '')].filter(Boolean) };
  }
  // Escopo de CODVENDs: PRIORIZA a equipe vinda do Sankhya (`equipeCodvends`, inclui vendedores que
  // NÃO são usuários do app); se ainda não foi sincronizada, cai nos vendedores-usuários + o próprio.
  // O CODVEND do próprio gestor entra sempre (p/ ver os clientes/visitas dele mesmo).
  // O .filter(Boolean) é essencial: um CODVEND vazio ('') casaria TODO cliente sem sankhya.codvend.
  const ownCode = String(user.codVendSankhya ?? '');
  const equipe = Array.isArray(user.equipeCodvends) ? user.equipeCodvends.map((c) => String(c)) : null;
  // FALLBACK ENXUTO (equipe ainda não sincronizada): SÓ o CODVEND próprio. Escopo montado localmente
  // NÃO é provável pela regra (`clienteDaEquipe()` só aceita equipeCodvends/Str ou o próprio) e regra
  // do Firestore não filtra: ela RECUSA a consulta inteira — o gestor ficaria com as telas VAZIAS em
  // vez de ver menos. Melhor pedir menos e receber do que pedir tudo e ser negado.
  const codvends = (equipe && equipe.length
    ? [...equipe, ownCode]
    : [ownCode]
  ).filter(Boolean);
  // sellerIds (usado p/ filtrar CHECK-INS, que só têm sellerId).
  // PRIORIZA o `equipeSellerIds` gravado pelo servidor (_computarEquipes): é a MESMA lista que as
  // firestore.rules validam. Se a consulta usasse um conjunto diferente do da regra, o Firestore
  // recusaria a query inteira e o gestor não veria NADA.
  // FALLBACK = SÓ o próprio uid. Calcular a equipe aqui no cliente (por hierarquia/CODVEND) era o
  // fallback antigo e funcionava enquanto staff lia tudo; com a regra apertada (Etapas 2 e 3) virou uma consulta que a
  // regra não consegue provar → o Firestore recusa TUDO e o gestor não vê nem os próprios dados.
  // O piso equivalente foi posto na regra (`sellerId == uid` / `createdBy == uid`), para o gestor sem
  // equipe computada (ex.: sem CODVEND) pelo menos enxergar o que ele mesmo cadastrou.
  const equipeIds = Array.isArray(user.equipeSellerIds) ? user.equipeSellerIds : null;
  const sellerIds = equipeIds && equipeIds.length
    ? Array.from(new Set<string>([user.id, ...equipeIds]))
    : [user.id];
  return { full: false, sellerIds, codvends };
}

export interface Client {
  id: string;
  name: string; // Razão Social
  tradeName?: string; // Nome Fantasia
  cnpj?: string;
  cpf?: string;
  sellerId?: string; // Vendedor responsável (uid)
  sellerName?: string;
  createdBy?: string;
  sankhya?: {
    status?: string; // synced | error | duplicate
    codparc?: number | string;
    codvend?: number | string;
    message?: string;
    error?: string;
    lastPurchaseAt?: Date | null;      // última compra (TGFCAB.DTFATUR), cache da Sankhya
    lastPurchaseSyncedAt?: Date | null; // quando o cache foi atualizado
    rede?: string | number | null;     // AD_CODIGO (REDE)
    grupo?: string | number | null;    // AD_GRUPO (GRUPO)
    redeNome?: string | null;          // AD_REDEGRUPO.NOME da rede
    grupoNome?: string | null;         // AD_REDEGRUPO.NOME do grupo
    contatoNome?: string | null;       // contato PRINCIPAL (TGFCTT, AD_PRINCIPAL='S') — por quem perguntar
    telefones?: string[];              // telefones alternativos (AD_TELEF2/3/4 + AD_TELEVENDAS)
  };
  lastCheckinAt?: Date | null; // última visita (desnormalizado dos checkins)
  cadastroPending?: boolean;   // tem pendência de cadastro (verificação automática)
  cadastroIssues?: string[];   // códigos das pendências encontradas (CadastroIssue)
  cadastroMissingFields?: string[]; // quais campos obrigatórios estão faltando
  cadastroCheckedAt?: Date | null; // quando a verificação rodou neste cliente
  // Roteirização inteligente:
  estrelas?: 0 | 1 | 2 | 3;    // potencial do cliente (0-3), define a cadência de visita
  potencialCompra?: string | null; // observações do potencial de compra (importado da planilha; editável)
  potencialVenda?: number | null;  // venda projetada em R$ (coluna venda_projetada da planilha)
  cooldownAte?: Date | null;   // congelado até esta data (resultado "comprador ausente"/"não atendeu")
  ultimoResultado?: 'comprador_atendeu' | 'comprador_ausente' | 'comprador_nao_atendeu' | null;
  ultimaVisitaResumo?: string | null; // resumo da última visita (seed via planilha)
  stateRegistration?: string; // Inscrição Estadual
  simplesNacional?: boolean;   // Optante do Simples Nacional
  phone?: string;
  email?: string;
  address: {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
  };
  location?: {
    latitude: number;
    longitude: number;
  };
  geoPrecision?: 'gps' | 'street' | 'postal' | 'erp' | 'city'; // qualidade da coordenada (gps = confirmada em campo no check-in, mais confiável; street/postal = preciso; erp = coordenada do Sankhya, tratada como precisa e revalidada em 2º plano; city = centro da cidade)
  locationSource?: 'checkin' | string; // origem da coordenada quando confirmada em campo
  locationUpdatedBy?: string; // uid do vendedor que confirmou a localização no check-in
  geoCity?: string | null; // cidade que a coordenada resolveu (validação cruzada com o endereço)
  notes?: string;
  createdAt: Date;
  active: boolean;
}

export interface AppSettings {
  checkinRadiusEnabled: boolean;
  checkinRadiusMeters: number;
  requireNotes: boolean;
  allowClientRegistrationBySeller: boolean;
  routeRecencyGreenDays: number;  // até X dias = verde (visitou/comprou recente)
  routeRecencyYellowDays: number; // até X dias = amarelo (atenção); acima = vermelho
  cadastroCheckEnabled: boolean;  // rotina automática de verificação de cadastro ligada
  cadastroCheckHour: number;      // hora (0-23, fuso SP) em que a rotina roda
  cadastroForgottenDays: number;  // dias sem visita para o cliente ser "esquecido"
  ultimaCompraAutoEnabled: boolean; // rotina diária de "dias sem comprar" (Sankhya)
  ultimaCompraHour: number;         // hora da rotina de última compra
  geocodeAutoEnabled: boolean;      // rotina diária de geocodificação
  geocodeHour: number;              // hora da rotina de geocodificação
  importCarteiraAutoEnabled: boolean; // (legado, sem UI) — superado pelo Sincronizar
  importCarteiraHour: number;
  importEquipesAutoEnabled: boolean;  // (legado, sem UI) — superado pelo Sincronizar
  importEquipesHour: number;
  sincronizarAutoEnabled: boolean;    // rotina diária do "Sincronizar Clientes" (espelho oficial)
  sincronizarHour: number;            // hora da rotina de sincronização
  sincDeleteGraceDays: number;        // dias desativado até APAGAR de vez (carência; 0 = nunca apaga)
  sincIncrementalEnabled: boolean;    // nas horas que NÃO são a da rodada completa, busca só o que mudou no ERP (PAR.DTALTER)
  // Rastreamento de EXPEDIENTE (contínuo em background). Diferente dos *Hour acima (inteiros 0-23):
  // trackingStartHour/EndHour são strings 'HH:MM' e o gate é no APARELHO (hora+min local), não no servidor.
  trackingExpedienteEnabled: boolean; // master on/off do rastreamento de expediente (default false)
  trackingStartHour: string;          // início da janela, 'HH:MM'
  trackingEndHour: string;            // fim da janela, 'HH:MM'
  trackingDays: number[];             // dias com rastreamento (getDay: 0=dom..6=sáb); fixo seg-sex [1..5]
}

export const DEFAULT_SETTINGS: AppSettings = {
  checkinRadiusEnabled: false,
  checkinRadiusMeters: 200,
  requireNotes: false,
  allowClientRegistrationBySeller: true,
  routeRecencyGreenDays: 15,
  routeRecencyYellowDays: 30,
  cadastroCheckEnabled: true,
  cadastroCheckHour: 7,
  cadastroForgottenDays: 30,
  ultimaCompraAutoEnabled: true,
  ultimaCompraHour: 5,
  geocodeAutoEnabled: true,
  geocodeHour: 4,
  importCarteiraAutoEnabled: false,
  importCarteiraHour: 3,
  importEquipesAutoEnabled: false,
  importEquipesHour: 2,
  sincronizarAutoEnabled: false,
  sincronizarHour: 3,
  sincDeleteGraceDays: 7,
  sincIncrementalEnabled: false,      // estreia DESLIGADO: ligar só depois de conferir uma rodada real
  trackingExpedienteEnabled: false,
  trackingStartHour: '07:30',
  trackingEndHour: '18:00',
  trackingDays: [1, 2, 3, 4, 5],
};

// Códigos de pendência de cadastro (espelham a Cloud Function verificarCadastros)
export type CadastroIssue = 'missingFields' | 'noGeo' | 'noSeller' | 'forgotten';

export const CADASTRO_ISSUE_LABELS: Record<CadastroIssue, string> = {
  missingFields: 'Campos obrigatórios faltando',
  noGeo: 'Sem geolocalização',
  noSeller: 'Sem vendedor responsável',
  forgotten: 'Cliente esquecido (sem visita)',
};

export function cadastroIssueLabel(code: string): string {
  return (CADASTRO_ISSUE_LABELS as Record<string, string>)[code] || code;
}

// Rótulos dos campos obrigatórios (para detalhar "campos faltando")
export const CADASTRO_FIELD_LABELS: Record<string, string> = {
  name: 'Razão social',
  document: 'CNPJ/CPF',
  street: 'Rua',
  number: 'Número',
  neighborhood: 'Bairro',
  city: 'Cidade',
  state: 'UF',
  zipCode: 'CEP',
  phone: 'Telefone',
};

export function cadastroFieldLabel(code: string): string {
  return CADASTRO_FIELD_LABELS[code] || code;
}

// Log de cada execução da verificação de cadastro
export interface CadastroCheck {
  id: string;
  runAt: Date;
  triggeredBy: 'schedule' | 'manual';
  forgottenDays: number;
  totalClients: number;
  pendingClients: number;
  byType: { missingFields: number; noGeo: number; noSeller: number; forgotten: number };
  durationMs?: number;
}

// Parâmetros do motor de Roteirização Inteligente (settings/roteirizacao)
export interface RoteirizacaoSettings {
  okinalarRedeCodigo: string;       // AD_CODIGO da rede OKINALAR no Sankhya
  ciclo3Dias: number;               // ciclo ideal por estrela (dias)
  ciclo2Dias: number;
  ciclo1Dias: number;
  janelaToleranciaDias: number;     // antecipar/adiar para otimizar geografia (± dias)
  multiplicadorRede: number;        // peso da Rede no ordenamento (modo padrão)
  cooldownContatoFrioDias: number;  // congelamento após contato frio
  jornadaInicio: string;            // "07:30"
  jornadaFim: string;               // "17:30"
  almocoMin: number;                // duração do almoço (min)
  almocoJanelaInicio: string;       // "12:00"
  almocoJanelaFim: string;          // "14:00"
  duracaoVisitaMin: number;         // média de atendimento por visita (min)
  velocidadeMediaKmh: number;       // p/ estimar tempo de deslocamento
  cidadesPulverizacao: string[];    // cidades "casa" do modo Pulverização
  // ---- ROTEIRO DA SEMANA (motor `smartWeekRoute`, no app) ----
  // Os três já existiam, mas como PADRÕES EMBUTIDOS no motor (`opts.detour ?? 1.4`, `?? 5`, `?? 4`):
  // calibrar exigia APK novo. Virando configuração, o gestor ajusta pelo painel. ⚠️ Os defaults abaixo
  // são EXATAMENTE os do motor — quem nunca tocar na tela continua com a rota que já tinha.
  fatorDesvio: number;              // distância de rua ÷ linha reta (Haversine). 1 = sem correção
  diasRota: number;                 // dias úteis planejados = nº de zonas do k-means
  minVisitasDia: number;            // piso de visitas/dia (a 2ª passagem enche até aqui)
  // Chave-mestra do rodízio de São Paulo. DESLIGADA por padrão: a regra tira clientes da rota, e uma
  // estreia ligada mudaria o roteiro de todo mundo sem ninguém ter pedido. Mesmo ligada, só age em
  // quem tem placa preenchida E tem cliente dentro do Centro Expandido — é auto-limitante.
  rodizioSpAtivo: boolean;
}

export const DEFAULT_ROTEIRIZACAO: RoteirizacaoSettings = {
  okinalarRedeCodigo: '',
  ciclo3Dias: 30,
  ciclo2Dias: 60,
  ciclo1Dias: 90,
  janelaToleranciaDias: 7,
  multiplicadorRede: 1.8,
  cooldownContatoFrioDias: 60,
  jornadaInicio: '07:30',
  jornadaFim: '17:30',
  almocoMin: 60,
  almocoJanelaInicio: '12:00',
  almocoJanelaFim: '14:00',
  duracaoVisitaMin: 30,
  velocidadeMediaKmh: 35,
  cidadesPulverizacao: ['Suzano', 'Mogi das Cruzes', 'Itaquaquecetuba', 'Ferraz de Vasconcelos'],
  fatorDesvio: 1.4,   // = `opts.detour ?? 1.4` em smartWeekRoute.ts
  diasRota: 5,        // = `opts.ndays ?? 5`
  minVisitasDia: 4,   // = `opts.minVisitas ?? 4`
  rodizioSpAtivo: false,
};

// Posição ao vivo do vendedor durante a rota
export interface LiveLocation {
  sellerId: string;
  sellerName?: string;
  lat: number;
  lng: number;
  active: boolean;
  routeId?: string | null;
  updatedAt: Date;
  // Trajeto até o cliente (rastreamento ao vivo):
  destClientId?: string | null;
  destClientName?: string | null;
  destLat?: number | null;
  destLng?: number | null;
  startLat?: number | null;
  startLng?: number | null;
  startedAt?: Date | null; // horário de partida (início da navegação)
  suggestedRoute?: { lat: number; lng: number }[]; // menor percurso (ORS)
  suggestedDurationSeconds?: number | null; // duração ORS da rota (p/ ETA preciso)
  suggestedDistanceMeters?: number | null;  // distância ORS da rota (p/ escalar o ETA)
  trail?: { lat: number; lng: number; t?: number }[]; // trajeto real do vendedor
}

// Rota planejada pelo vendedor
export interface RouteStop {
  clientId: string;
  codparc?: number | string;
  name: string;
  order: number;
  lat: number;
  lng: number;
}

export interface Route {
  id: string;
  sellerId: string;
  name: string;
  description?: string; // descrição opcional da rota (definida pelo usuário)
  stops: RouteStop[];
  origin: { lat: number; lng: number };
  optimizer: 'ors' | 'local';
  distanceMeters?: number;
  durationSeconds?: number;
  seenBySeller?: boolean; // false = rota criada pelo gestor ainda não vista pelo vendedor
  createdAt: Date;
}

export interface CheckIn {
  id: string;
  sellerId: string;
  sellerName: string;
  clientId: string;
  clientName: string;
  location: {
    latitude: number;
    longitude: number;
    accuracy?: number;
  };
  notes?: string;
  reasons?: string[]; // Motivos do check-in
  photos?: string[]; // URLs das fotos do check-in (Firebase Storage)
  checkoutPhotos?: string[]; // URLs das fotos do check-out
  checkoutAt?: Date | null; // Check-out (fim da visita)
  // POSSÍVEL DUPLICIDADE, marcada pelo SERVIDOR (functions: marcarVisitasDuplicadas): existe outra
  // visita ABERTA do mesmo vendedor no mesmo cliente, a menos de 12 h. São campos puramente
  // INFORMATIVOS — nada é fechado, nada sai do conjunto aberto e nenhum relatório os lê. Quem resolve
  // é quem sabe o que aconteceu; o servidor só levanta a mão. A marca some sozinha quando uma das
  // visitas recebe check-out (functions: limparMarcaDuplicada).
  possivelDuplicada?: boolean | null;
  possivelDuplicadaDe?: string[] | null; // ids das OUTRAS visitas abertas do grupo
  possivelDuplicadaEm?: Date | null;
  resultado?: 'comprador_atendeu' | 'comprador_ausente' | 'comprador_nao_atendeu' | null; // resultado da visita (no check-out)
  foraDoRaio?: boolean;      // check-in feito além do raio permitido (coordenada do cliente pode estar errada)
  distanciaMetros?: number | null; // distância vendedor↔cliente no check-in (m)
  pedidoStatus?: 'sim' | 'nao' | 'orcamento'; // resultado comercial da visita
  pedidoFeito?: boolean;     // houve pedido confirmado nesta visita (= pedidoStatus 'sim')
  pedidoValor?: number | null; // valor do pedido/orçamento (R$)
  trajeto?: {                // histórico do percurso até o cliente (partida → chegada)
    startLat?: number | null; startLng?: number | null;
    startedAt?: Date | null;  // horário em que o vendedor iniciou a navegação
    destLat?: number | null; destLng?: number | null;
    suggestedRoute?: { lat: number; lng: number }[];
    suggestedDurationSeconds?: number | null; // estimativa do ORS (duração da rota sugerida)
    trail?: { lat: number; lng: number; t?: number }[];
  } | null;
  createdAt: Date;
}

export interface CheckinReason {
  id: string;
  name: string;
  active: boolean;
}

// Helpers de exibição (espelhados web/app)
export function clientDisplayName(c: { name: string; tradeName?: string; sankhya?: { codparc?: number | string } }): string {
  const cod = c.sankhya?.codparc;
  const nome = c.name || c.tradeName || ''; // sempre Razão Social (cai para fantasia só se a razão estiver vazia)
  return cod ? `${cod} - ${nome}` : nome;
}

export function sellerDisplayName(u: { name: string; codVendSankhya?: number }): string {
  return u.codVendSankhya ? `${u.codVendSankhya} - ${u.name}` : u.name;
}

// Status da sincronização com a Sankhya (rótulo + cores) — espelhado web/app
export function sankhyaStatusInfo(sankhya?: { status?: string }): { label: string; color: string; bg: string } {
  switch (sankhya?.status) {
    case 'synced': return { label: 'Sincronizado', color: '#16a34a', bg: '#dcfce7' };
    case 'duplicate': return { label: 'Duplicado', color: '#d97706', bg: '#fef3c7' };
    case 'error': return { label: 'Erro', color: '#dc2626', bg: '#fee2e2' };
    case 'nao_cadastrado': return { label: 'Não cadastrado no Sankhya', color: '#7c3aed', bg: '#ede9fe' };
    default: return { label: 'Pendente', color: '#6b7280', bg: '#f3f4f6' };
  }
}
